import { followingSidebar } from '../../utils/sidebar.utils.js';
import {
  clearJobsFilterUrl,
  replaceJobsFilterUrl,
} from '../../utils/jobs-search-query.utils.js';

const searchSection = document.querySelector('.search-sec');
const searchSidebarDesktopBreakpoint = 902;

if (searchSection) {
  const sidebar = searchSection.querySelector('#sidebar');
  const contentSticky = searchSection.querySelector('#content-sticky');
  const filtersBtn = searchSection.querySelector('#filters-btn');
  const contentResult = searchSection.querySelector('#content-result');
  const fourthCircle = searchSection.querySelector('.search-sec__circle:nth-child(4)');
  const fourthCircleContainer = fourthCircle?.parentElement;

  const isColumnTypeSec = searchSection.classList.contains('column-type');
  let followingSidebarInstance = null;
  let fourthCircleVisibilityFrame = null;

  const updateFourthCircleVisibility = () => {
    if (!fourthCircle || !fourthCircleContainer) return;

    const shouldHideFourthCircle =
      window.innerWidth > 640 &&
      fourthCircle.offsetTop + fourthCircle.offsetHeight > fourthCircleContainer.clientHeight;

    searchSection.classList.toggle('is-fourth-circle-hidden', shouldHideFourthCircle);
  };

  const scheduleFourthCircleVisibilityUpdate = () => {
    if (fourthCircleVisibilityFrame !== null) return;

    const requestFrame =
      window.requestAnimationFrame?.bind(window) ?? window.setTimeout.bind(window);

    fourthCircleVisibilityFrame = requestFrame(() => {
      fourthCircleVisibilityFrame = null;
      updateFourthCircleVisibility();
    });
  };

  if (fourthCircle && fourthCircleContainer) {
    scheduleFourthCircleVisibilityUpdate();
    window.addEventListener?.('load', scheduleFourthCircleVisibilityUpdate, { once: true });
    window.addEventListener?.('resize', scheduleFourthCircleVisibilityUpdate, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      const fourthCircleResizeObserver = new ResizeObserver(
        scheduleFourthCircleVisibilityUpdate
      );

      fourthCircleResizeObserver.observe(fourthCircleContainer);
    }
  }

  const updateFollowingSidebar = () => {
    const shouldFollowSidebar =
      sidebar &&
      contentSticky &&
      window.innerWidth >= searchSidebarDesktopBreakpoint &&
      !isColumnTypeSec;

    if (!shouldFollowSidebar) {
      followingSidebarInstance?.destroy();
      followingSidebarInstance = null;
      return;
    }

    followingSidebarInstance ??= followingSidebar({ sidebar, contentSticky });
    followingSidebarInstance?.refresh();
  };

  const refreshFollowingSidebar = () => {
    followingSidebarInstance?.refresh();
  };

  const refreshFollowingSidebarConstraint = () => {
    followingSidebarInstance?.refreshConstraint();
  };

  const accordionResizeTransitions = new Map();
  const accordionResizeTransitionTimeout = 400;

  const endAccordionResizeTransition = (content) => {
    const timeout = accordionResizeTransitions.get(content);

    if (timeout === undefined) return;

    window.clearTimeout(timeout);
    accordionResizeTransitions.delete(content);

    if (!accordionResizeTransitions.size) {
      followingSidebarInstance?.endResizeTransition();
    }
  };

  const beginAccordionResizeTransition = (content) => {
    if (!content || !followingSidebarInstance) return;

    const previousTimeout = accordionResizeTransitions.get(content);

    if (previousTimeout !== undefined) {
      window.clearTimeout(previousTimeout);
    } else if (!accordionResizeTransitions.size) {
      followingSidebarInstance.beginResizeTransition();
    }

    const timeout = window.setTimeout(
      () => endAccordionResizeTransition(content),
      accordionResizeTransitionTimeout
    );

    accordionResizeTransitions.set(content, timeout);
  };

  if (sidebar && contentSticky) {
    updateFollowingSidebar();
    window.addEventListener?.('resize', updateFollowingSidebar, {passive: true});
  }

  if (filtersBtn && sidebar && contentSticky && contentResult) {
    filtersBtn.addEventListener('click', () => {
      filtersBtn.classList.toggle('is-open-filters');
      sidebar.classList.toggle('is-open-filters');
      contentSticky.classList.toggle('is-open-filters');
      contentResult.classList.toggle('is-open-filters');
    });

    contentResult.addEventListener('click', () => {
      if (contentResult.classList.contains('is-open-filters')) {
        filtersBtn.classList.remove('is-open-filters');
        sidebar.classList.remove('is-open-filters');
        contentSticky.classList.remove('is-open-filters');
        contentResult.classList.remove('is-open-filters');
      }
    });
  }

  const sidebarAccordionButtonsArr = searchSection.querySelectorAll('.search-sec__sidebar-item-btn');

  if (sidebarAccordionButtonsArr.length) {
    sidebarAccordionButtonsArr.forEach((btn) => {
      const item = btn.closest('.search-sec__sidebar-item-toggle');
      const toggleEl = item;

      if (!toggleEl) return;

      if (!toggleEl.classList.contains('is-active')) {
        item.classList.add('is-closed');
      }

      const toggleItem = () => {
        const isOpen = toggleEl.classList.contains('is-active');

        toggleEl.classList.toggle('is-active', !isOpen);
        item.classList.toggle('is-closed', isOpen);
        beginAccordionResizeTransition(toggleEl.nextElementSibling);
        refreshFollowingSidebarConstraint();
      };

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        toggleItem();
      });

      item.addEventListener('click', () => {
        toggleItem();
      });
    });
  }

  const finishAccordionResizeTransition = (event) => {
    if (
      event.propertyName === 'max-height' &&
      event.target.classList.contains('search-sec__sidebar-item-content')
    ) {
      endAccordionResizeTransition(event.target);
    }
  };

  searchSection.addEventListener('transitionend', finishAccordionResizeTransition);
  searchSection.addEventListener('transitioncancel', finishAccordionResizeTransition);

  const form = searchSection.querySelector('[data-jobs-search-form]');

  if (form) {
    const searchDebounceDelay = 450;
    let searchDebounceTimer;
    let isBusy = false;

    const associatedControls = () =>
      Array.from(document.querySelectorAll(`[form="${form.id}"]`));
    const jobsIndexUrl = form.dataset.jobsIndexUrl;
    const sectorUrls = new Map(
      associatedControls()
        .filter(
          (control) => control.name === 'sectors[]' && control.dataset.jobsSectorUrl
        )
        .map((control) => [String(control.value), control.dataset.jobsSectorUrl])
    );

    const responseHasErrors = (response) =>
      Array.isArray(response?.error) ? response.error.length > 0 : Boolean(response?.error);

    const syncFilterUrl = (clearOnly = false) => {
      const url = new URL(window.location.href);

      if (clearOnly) {
        clearJobsFilterUrl(url, jobsIndexUrl);
      } else {
        replaceJobsFilterUrl(
          url,
          new FormData(form).entries(),
          jobsIndexUrl,
          sectorUrls
        );
      }

      window.history.replaceState(window.history.state, '', url);
    };

    const submitSearch = async (page, { append = false, clear = false } = {}) => {
      if (isBusy) return;

      isBusy = true;
      form.setAttribute('aria-busy', 'true');

      const loadMoreButton = searchSection.querySelector('#load-more-block [data-jobs-page]');
      loadMoreButton?.setAttribute('disabled', 'disabled');

      try {
        if (typeof window.loadWithResponse !== 'function') {
          throw new Error('Jobs search transport is unavailable.');
        }

        const response = await window.loadWithResponse(
          form.dataset.jobsSearchUrl,
          `form:#${form.id}`,
          `page=${page}`
        );

        if (!response || responseHasErrors(response)) return;

        if (typeof window.processField === 'function') {
          (response.res || []).forEach((action) => window.processField(action));
        }

        refreshFollowingSidebar();
        scheduleFourthCircleVisibilityUpdate();

        if (!append && typeof window.scrollToEl === 'function') {
          const headerOffset = document.querySelector('.header')?.offsetHeight ?? 0;

          window.scrollToEl(`#search_results|800|${headerOffset + 24}`);
        }

        if (!append) syncFilterUrl(clear);
      } catch {
        window.noticeError?.('Unable to load vacancies. Please try again.');
      } finally {
        isBusy = false;
        form.removeAttribute('aria-busy');
        loadMoreButton?.removeAttribute('disabled');
      }
    };

    searchSection.addEventListener('input', (event) => {
      if (
        event.target.getAttribute('form') !== form.id ||
        event.target.getAttribute('name') !== 'keywords'
      ) {
        return;
      }

      window.clearTimeout(searchDebounceTimer);
      searchDebounceTimer = window.setTimeout(() => submitSearch(1), searchDebounceDelay);
    });

    searchSection.addEventListener('change', (event) => {
      if (
        event.target.getAttribute('form') !== form.id ||
        event.target.getAttribute('name') === 'keywords'
      ) {
        return;
      }

      submitSearch(1);
    });

    searchSection.addEventListener('click', (event) => {
      const clearControl = event.target.closest('[data-jobs-clear-filters]');

      if (clearControl) {
        event.preventDefault();
        window.clearTimeout(searchDebounceTimer);

        associatedControls().forEach((control) => {
          if (control.type === 'checkbox' || control.type === 'radio') {
            control.checked = false;
          } else {
            control.value = '';
          }
        });

        submitSearch(1, { clear: true });
        return;
      }

      const control = event.target.closest('#load-more-block [data-jobs-page]');

      if (!control) return;

      event.preventDefault();
      const page = Number.parseInt(control.dataset.jobsPage, 10);

      if (Number.isInteger(page) && page > 1) {
        submitSearch(page, { append: true });
      }
    });
  }
}
